import { ExternalLink, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HackerSponsorsElevenlabs } from '@/types';

const DISCORD_INVITE_URL = 'https://discord.com/invite/VnBvbbcdEC';
const TUTORIAL_EMBED_URL = 'https://www.youtube.com/embed/S143_JtCtV8';

export default function Elevenlabs({
  email,
  subtitle,
  eventLabel,
  notice,
  couponLinks,
  couponUrl,
}: HackerSponsorsElevenlabs) {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl space-y-8">
          <div className="text-center">
            <h1 className="inline-block font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
              <span className="bg-primary px-2 py-1 text-background">
                ELEVENLABS
              </span>
            </h1>
            <p className="mt-4 text-primary/70 text-sm uppercase tracking-wide">
              {subtitle}
            </p>
          </div>

          <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
            <div className="space-y-6">
              <div className="border-primary/20 border-b pb-4">
                <h2 className="font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
                  CRÉDITOS DE ELEVENLABS
                </h2>
                <p className="mt-2 text-primary/70 text-sm">
                  Cada hacker recibe un mes del Creator Tier de ElevenLabs.
                </p>
              </div>

              {couponLinks ? (
                <CouponLink couponUrl={couponUrl} />
              ) : (
                <DiscordRedemption
                  email={email}
                  eventLabel={eventLabel}
                  notice={notice}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CouponLink({ couponUrl }: { couponUrl: string | null }) {
  if (!couponUrl) {
    return (
      <div className="border-primary border-l-4 pl-4">
        <p className="text-primary/70 text-sm">
          Aún no hay un cupón de ElevenLabs asignado a tu cuenta. Si crees que
          esto es un error, avísanos en Discord.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border-primary border-l-4 pl-4">
        <h3 className="font-bold text-primary/70 text-sm uppercase">
          Tu cupón personal
        </h3>
        <p className="break-all font-mono text-primary text-sm sm:text-base">
          {couponUrl}
        </p>
        <p className="mt-2 text-primary/70 text-sm">
          El cupón es de un solo uso y es solo para ti: no lo compartas.
        </p>
      </div>

      <a
        href={couponUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 border-2 border-primary bg-primary px-6 py-3 font-bold text-background uppercase tracking-wide transition-all hover:bg-background hover:text-primary"
      >
        Canjear cupón
        <ExternalLink className="h-4 w-4" />
      </a>
    </>
  );
}

function DiscordRedemption({
  email,
  eventLabel,
  notice,
}: Pick<HackerSponsorsElevenlabs, 'email' | 'eventLabel' | 'notice'>) {
  return (
    <>
              {notice ? (
                <div
                  role="alert"
                  className="flex gap-3 border-2 border-primary p-4"
                >
                  <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <p className="whitespace-pre-line font-mono text-primary text-sm sm:text-base">
                    {notice}
                  </p>
                </div>
              ) : null}

              {/* With a notice the redemption steps stay visible but dead. */}
              <div
                inert={notice ? true : undefined}
                aria-disabled={notice ? true : undefined}
                className={cn(
                  'space-y-6',
                  notice && 'select-none opacity-40 grayscale',
                )}
              >
                <div className="border-primary border-l-4 pl-4">
                  <h3 className="font-bold text-primary/70 text-sm uppercase">
                    Cómo canjear tu crédito
                  </h3>
                  <ol className="mt-3 list-decimal space-y-2 pl-5 font-mono text-primary/80 text-sm sm:text-base">
                    <li>
                      Entra al servidor de Discord de ElevenLabs:{' '}
                      <a
                        href={DISCORD_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-primary underline hover:text-primary/80"
                      >
                        {DISCORD_INVITE_URL}
                      </a>
                    </li>
                    <li>
                      Ve al canal{' '}
                      <span className="font-bold text-primary">
                        #🎟️│coupon-codes
                      </span>
                      .
                    </li>
                    <li>
                      Presiona{' '}
                      <span className="font-bold text-primary">
                        “Start Redemption”
                      </span>
                      .
                    </li>
                    <li>
                      Selecciona{' '}
                      <span className="font-bold text-primary">{eventLabel}</span>{' '}
                      e ingresa el mail con el que te registraste a Platanus Hack
                      {email ? (
                        <>
                          {' '}
                          (<span className="text-primary">{email}</span>)
                        </>
                      ) : null}
                      .
                    </li>
                    <li>El bot te enviará tu código.</li>
                  </ol>
                </div>

                <a
                  href={DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-2 border-primary bg-primary px-6 py-3 font-bold text-background uppercase tracking-wide transition-all hover:bg-background hover:text-primary"
                >
                  Ir al Discord de ElevenLabs
                  <ExternalLink className="h-4 w-4" />
                </a>

                <div className="space-y-3 border-primary/20 border-t pt-6">
                  <h3 className="font-bold text-primary/70 text-sm uppercase">
                    Tutorial
                  </h3>
                  <div className="aspect-video w-full border-2 border-primary">
                    <iframe
                      src={TUTORIAL_EMBED_URL}
                      title="Tutorial de canje de créditos de ElevenLabs"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="h-full w-full"
                    />
                  </div>
                </div>
              </div>
    </>
  );
}
